import React, { useEffect } from "react";
import { Drawer, Form, Input, DatePicker, Button, Select } from 'antd';
import * as actions from "store/actions/AuthActions";
import { useNotification } from 'NotificationContext';
import dayjs from 'dayjs';
import useIsMobile from 'hooks/useIsMobile';

const { TextArea } = Input;
const { Option } = Select;

const AssetAssignmentForm = ({ visible, onClose, assetId, instance, employees, onSuccess }) => {
    const [form] = Form.useForm();
    const notify = useNotification();
    const isMobile = useIsMobile();

    useEffect(() => {
        if (visible) {
            if (instance) {
                const data = { ...instance };
                if (data.assigned_date) {
                    data.assigned_date = dayjs(data.assigned_date);
                }
                if (data.returned_date) {
                    data.returned_date = dayjs(data.returned_date);
                }
                form.setFieldsValue(data);
            } else {
                form.resetFields();
                form.setFieldsValue({
                    assigned_date: dayjs(),
                    assignment_type: 'PERMANENT'
                });
            }
        }
    }, [visible, instance, form]);

    const handleSubmit = async (values) => {
        const formattedValues = { ...values };
        formattedValues.asset = assetId;

        if (formattedValues.assigned_date) {
            formattedValues.assigned_date = dayjs(formattedValues.assigned_date).format('YYYY-MM-DD');
        }
        if (formattedValues.returned_date) {
            formattedValues.returned_date = dayjs(formattedValues.returned_date).format('YYYY-MM-DD');
        }

        try {
            if (instance) {
                const response = await actions.updateAssetAssignment(formattedValues, instance.id);
                if (response) {
                    notify("success", "Assignment updated successfully");
                    onSuccess();
                }
            } else {
                const response = await actions.addAssetAssignment(formattedValues);
                if (response && response.status === 201) {
                    notify("success", "Assignment created successfully");
                    onSuccess();
                }
            }
        } catch (error) {
            notify("error", "Failed to save assignment");
        }
    };

    return (
        <Drawer
            title={instance ? "Edit Assignment" : "Add Assignment"}
            placement={isMobile ? "bottom" : "right"}
            width={isMobile ? "100%" : 500}
            height={isMobile ? "90vh" : undefined}
            onClose={onClose}
            open={visible}
            styles={{
                body: { paddingBottom: isMobile ? 80 : 24 }
            }}
            footer={
                <div style={{ display: 'flex', justifyContent: isMobile ? 'stretch' : 'flex-end', gap: '8px' }}>
                    <Button onClick={onClose} style={isMobile ? { flex: 1 } : undefined}>Cancel</Button>
                    <Button type="primary" onClick={() => form.submit()} style={isMobile ? { flex: 1 } : undefined}>
                        Save
                    </Button>
                </div>
            }
        >
            <Form form={form} onFinish={handleSubmit} layout="vertical">
                <Form.Item
                    label="Employee"
                    name="employee"
                    rules={[{ required: true, message: 'Please select an employee' }]}
                >
                    <Select
                        showSearch
                        placeholder="Select employee"
                        optionFilterProp="children"
                        filterOption={(input, option) =>
                            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                        }
                        options={employees.map(emp => {
                            const userName = emp.user_name || '';
                            const cleanName = userName.split(' ')
                                .filter(part => part && part !== 'None')
                                .join(' ')
                                .trim();
                            return {
                                value: emp.id,
                                label: `${cleanName || 'Unknown'} (${emp.employee_id || 'N/A'})`
                            };
                        })}
                    />
                </Form.Item>

                <Form.Item
                    label="Assigned Date"
                    name="assigned_date"
                    rules={[{ required: true, message: 'Please select assigned date' }]}
                >
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item label="Returned Date" name="returned_date">
                    <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>

                <Form.Item
                    label="Assignment Type"
                    name="assignment_type"
                    rules={[{ required: true, message: 'Please select assignment type' }]}
                >
                    <Select>
                        <Option value="PERMANENT">Permanent</Option>
                        <Option value="TEMPORARY">Temporary</Option>
                        <Option value="LOAN">Loan</Option>
                    </Select>
                </Form.Item>

                <Form.Item label="Condition at Assignment" name="condition_at_assignment">
                    <TextArea rows={3} placeholder="Describe the condition of the asset..." />
                </Form.Item>

                <Form.Item label="Condition at Return" name="condition_at_return">
                    <TextArea rows={3} placeholder="Describe the condition when returned..." />
                </Form.Item>

                <Form.Item label="Notes" name="notes">
                    <TextArea rows={3} placeholder="Additional notes..." />
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default AssetAssignmentForm;
